import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardCheck, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import SubtleBadge from "components/common/SubtleBadge";
import { GRACE_NEVER_BLOCKS } from "./auditApi";

/**
 * Group-level periodic audit settings (INVUI-701). Controlled: the parent owns
 * the value and saves it with the rest of the group defaults.
 *
 *  - Grace is a sentinel, not a plain number: -999 = never blocks. The value
 *    received is sent back untouched unless the user changes it.
 *  - "Block medication pass" has no consumer yet, so it is shown but cannot
 *    be switched on here.
 *  - audit_can_block is worked out by the server and only displayed.
 *  - "How often" is days plus optional hours, e.g. every 1 day 12 hrs.
 *    An empty box counts as 0.
 */

// Fixed widths so the number boxes never get squeezed and cut the digits off.
// No error icon inside them either — it would cover the number in a box this
// small; the red border and the message underneath show the problem.
const DAYS_BOX = { flex: "0 0 72px", width: 72, backgroundImage: "none" };
const HOURS_BOX = { flex: "0 0 60px", width: 60, backgroundImage: "none" };

// Whole number between 0 and max; empty counts as 0. null when invalid.
const wholeNumber = (v, max = Infinity) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 && n <= max ? n : null;
};

export const validateAuditSettings = (cfg) => {
  const errors = {};
  if (!cfg || !cfg.enabled) return errors;

  const days = wholeNumber(cfg.intervalDays);
  const hours = wholeNumber(cfg.intervalHours, 23);
  if (days === null) {
    errors.intervalDays = "Days must be a whole number.";
  } else if (hours === null) {
    errors.intervalHours = "Hours must be between 0 and 23.";
  } else if (days * 24 + hours < 1) {
    errors.intervalDays = "Set how often audits run, e.g. every 1 day 12 hrs.";
  }

  if (Number(cfg.graceDays) !== GRACE_NEVER_BLOCKS) {
    const grace = Number(cfg.graceDays);
    if (cfg.graceDays === "" || !Number.isInteger(grace) || grace < 0) {
      errors.graceDays = "Grace must be 0 days or more.";
    }
  }
  return errors;
};

const AuditSettings = ({ value, onChange, errors = {} }) => {
  // Last real grace figure, so switching "never blocks" off restores it.
  const lastGraceRef = useRef(0);
  const neverBlocks = value ? Number(value.graceDays) === GRACE_NEVER_BLOCKS : true;

  useEffect(() => {
    if (value && !neverBlocks && value.graceDays !== "") {
      lastGraceRef.current = Number(value.graceDays);
    }
  }, [value, neverBlocks]);

  if (!value) return null;

  const set = (patch) => onChange({ ...value, ...patch });

  const numberOrBlank = (raw) => (raw === "" ? "" : Number(raw));

  // 0 shows as an empty box (placeholder "0").
  const shown = (n) => (n === 0 ? "" : n);

  const intervalError = errors.intervalDays || errors.intervalHours;

  return (
    <div className="mt-4 pt-4 border-top border-200">
      <Row className="align-items-center g-3">
        <Col xs={12} sm>
          <h6 className="mb-1 fw-bold text-900 fs-10">
            <FontAwesomeIcon icon={faClipboardCheck} className="text-primary me-2" />
            Periodic audit
          </h6>
          <p className="text-600 mb-0 fs-10">
            On a regular schedule, staff count every tracked medication a patient has, with a photo,
            to check stock against the records.
          </p>
        </Col>
        <Col xs={12} sm="auto">
          <div className="d-inline-flex align-items-center gap-2">
            <Form.Check
              type="switch"
              id="group-audit-enabled"
              className="mb-0"
              style={{ fontSize: "1.35rem" }}
              checked={Boolean(value.enabled)}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
            <SubtleBadge bg={value.enabled ? "success" : "secondary"} pill>
              {value.enabled ? "On" : "Off"}
            </SubtleBadge>
          </div>
        </Col>
      </Row>

      {value.enabled && (
        <div className="mt-3 p-3 rounded-3 border border-300 bg-body-tertiary">
          <Row className="g-4">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold fs-10 mb-1">How often</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                <InputGroup size="sm" className="w-auto flex-nowrap">
                  <InputGroup.Text className="px-2">Every</InputGroup.Text>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    aria-label="Days"
                    className="px-2"
                    style={DAYS_BOX}
                    value={shown(value.intervalDays)}
                    onChange={(e) => set({ intervalDays: numberOrBlank(e.target.value) })}
                    isInvalid={Boolean(errors.intervalDays)}
                  />
                  <InputGroup.Text className="px-2">days</InputGroup.Text>
                </InputGroup>
                <InputGroup size="sm" className="w-auto flex-nowrap">
                  <Form.Control
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    placeholder="0"
                    aria-label="Hours"
                    className="px-2"
                    style={HOURS_BOX}
                    value={shown(value.intervalHours)}
                    onChange={(e) => set({ intervalHours: numberOrBlank(e.target.value) })}
                    isInvalid={Boolean(errors.intervalHours)}
                  />
                  <InputGroup.Text className="px-2">hours</InputGroup.Text>
                </InputGroup>
              </div>
              {intervalError ? (
                <div className="invalid-feedback d-block">{intervalError}</div>
              ) : (
                <Form.Text className="text-600 fs-11">For example, every 1 day 12 hours.</Form.Text>
              )}
            </Col>

            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold fs-10 mb-1">When an audit is due</Form.Label>
              <Form.Check
                type="switch"
                id="group-audit-never-blocks"
                className="fs-10"
                checked={neverBlocks}
                onChange={(e) =>
                  set({
                    graceDays: e.target.checked ? GRACE_NEVER_BLOCKS : lastGraceRef.current
                  })
                }
                label="Never blocks — it stays a reminder that staff can always skip"
              />
              {!neverBlocks && (
                <div className="mt-2">
                  <InputGroup size="sm" style={{ maxWidth: 260 }} hasValidation>
                    <InputGroup.Text>Grace</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min={0}
                      step={1}
                      value={value.graceDays}
                      onChange={(e) => set({ graceDays: numberOrBlank(e.target.value) })}
                      isInvalid={Boolean(errors.graceDays)}
                    />
                    <InputGroup.Text>days</InputGroup.Text>
                    <Form.Control.Feedback type="invalid">{errors.graceDays}</Form.Control.Feedback>
                  </InputGroup>
                  <Form.Text className="text-600 fs-11">
                    Days after the due date before it counts as overdue. 0 means overdue the moment
                    it&apos;s due.
                  </Form.Text>
                </div>
              )}
            </Col>
          </Row>

          {/* <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top border-300">
            <Form.Check
              type="switch"
              id="group-audit-blocks-medpass"
              className="fs-10 mb-0 opacity-75"
              checked={Boolean(value.blocksMedpass)}
              disabled
              onChange={() => {}}
              label="Block medication pass when an audit is overdue"
            />
            <SubtleBadge bg="secondary" pill>
              Not yet active
            </SubtleBadge>
          </div>
          <div className="text-600 fs-11 mt-1">
            Nothing in the system uses this setting yet, so an audit can&apos;t stop anyone giving
            medication.
          </div>

          <div className="text-700 fs-11 mt-2">
            <FontAwesomeIcon icon={faInfoCircle} className="me-1 text-info" />
            Could an overdue audit block anything with these settings?{" "}
            <span className="fw-semibold">{value.canBlock ? "Yes" : "No"}</span>
            <span className="text-600"> — worked out by the server.</span>
          </div> */}
        </div>
      )}
    </div>
  );
};

AuditSettings.propTypes = {
  value: PropTypes.shape({
    enabled: PropTypes.bool,
    intervalDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    intervalHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    graceDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    blocksMedpass: PropTypes.bool,
    canBlock: PropTypes.bool
  }),
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object
};

export default AuditSettings;
